/* Setting somebody up, one step at a time.
 *
 * WHY THIS IS NOT EditDrawer
 *
 * EditDrawer is one scrolling form with one Save, and that is right for a
 * register row: the whole thing lands or none of it does. Onboarding is not
 * that. It writes a depot to SharePoint, a group membership to Entra, a device
 * assignment back to SharePoint and then asks a Function for a folder and an
 * email — four systems, each of which can fail while the others succeed. A
 * single Save button has no way to say "three of five worked", and that is the
 * state this flow spends most of its life in.
 *
 * So each step saves itself and reports its own outcome. The form steps still
 * use FieldInput and the field definitions from registerFields.ts, because a
 * second set of date inputs that behave slightly differently is exactly the
 * kind of divergence this codebase keeps paying for.
 *
 * NOTHING HERE STAMPS ITSELF
 *
 * Finish is a button a person presses. The flow never decides it is complete
 * because the rows all went green — those are different claims, and only the
 * second one closes a case that half-failed.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  canFinishOnboarding,
  isOffboarding,
  offboardingSteps,
  onboardingSteps,
  type LifecycleStep,
} from '../../lib/lifecycle';
import { traitsFor } from '../../lib/deviceGroups';
import { AccessPanel } from './AccessPanel';
import { AlreadySetUp } from './AlreadySetUp';
import { SystemsPanel } from './SystemsForm';
import { isRealPerson } from '../../lib/registers';
import { WindDownPanel } from './WindDownPanel';
import { personFields, valuesSeen } from '../../lib/registerFields';
import type { IDepot, IDevice, IPerson, Tone } from '../../models';
import { leaveSourceFor } from '../../lib/leaveRecord';
import type { ILeaveOutcome } from '../../services/graph/LeaveSyncClient';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import { Badge, Button, Drawer, useToast, type ToastHandle } from '../ui';
import { FieldInput, type RenderableField } from './RecordField';
import s from './screens.module.scss';

/** One row in the flow: what it is, where it stands, and what you can do now.
 *
 *  Kept presentational on purpose — every decision about state lives in
 *  lib/lifecycle.ts, where a test can reach it without rendering anything. */
function StepRow({
  step,
  children,
}: {
  step: LifecycleStep;
  children?: React.ReactNode;
}): JSX.Element {
  const tone: Tone =
    step.state === 'done' ? 'ok'
      : step.state === 'blocked' ? 'bad'
        : step.state === 'n/a' ? 'info'
          : 'warn';
  const label =
    step.state === 'done' ? 'Done'
      : step.state === 'blocked' ? 'Blocked'
        : step.state === 'n/a' ? 'Not needed'
          : 'To do';

  return (
    <div className={s.stepRow}>
      <div className={s.stepHead}>
        <div className={s.stepLabel}>{step.label}</div>
        <Badge tone={tone}>{label}</Badge>
      </div>
      <div className={s.stepDetail}>{step.detail}</div>
      {children ? <div className={s.stepBody}>{children}</div> : null}
    </div>
  );
}

/** The fields a step asks for, drawn by the same renderer the registers use. */
function FormBlock({
  fields,
  values,
  onChange,
  lookups,
}: {
  fields: RenderableField[];
  values: Record<string, unknown>;
  onChange: (id: string, v: unknown) => void;
  lookups: { people: IPerson[]; suppliers: never[] };
}): JSX.Element {
  return (
    <div className={s.stepFields}>
      {fields.map((f) => (
        <FieldInput
          key={f.id}
          field={f}
          value={values[f.id]}
          onChange={(v) => onChange(f.id, v)}
          lookups={lookups}
          values={values}
        />
      ))}
    </div>
  );
}

export function LifecycleDrawer({
  person,
  devices,
  depots,
  onClose,
}: {
  person: IPerson | null;
  devices: readonly IDevice[];
  depots: readonly IDepot[];
  onClose: () => void;
}): JSX.Element | null {
  const svc = useServices();
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [kit, setKit] = useState('');

  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list(), enabled: false });

  /* Re-read from the query rather than trusting what the row looked like when
     it was clicked: a step that just wrote something must show the result, not
     the state before it. */
  const live = useMemo(
    () => (person ? (people.data ?? []).find((p) => p.id === person.id) ?? person : null),
    [person, people.data],
  );

  useEffect(() => {
    if (!live) return;
    setValues({
      kind: live.kind ?? '',
      loc: live.loc ?? '',
      mobile: live.mobile ?? '',
      start: live.start ?? '',
      title: live.title ?? '',
      dept: live.dept ?? '',
    });
    // Only when a different person is opened; typing must not be reset by a refetch.
  }, [live?.id]);

  const save = useMutation({

    meta: { quiet: true },
    mutationFn: (patch: Partial<IPerson>) => svc.people.update(live!.id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.people });
      void qc.invalidateQueries({ queryKey: qk.audit });
    },
  });

  /* Live, never the nightly sync's copy. The queue shows a level from last
     night because a badge can be a day old; a decision cannot, and this is the
     thing that decides what the Access step offers. */
  const directory = useQuery({
    queryKey: qk.directoryAccess(live?.graphId ?? ''),
    queryFn: () => svc.directory.get(live!.graphId as string),
    enabled: !!live?.graphId,
  });

  /* setLevel and setAppRole moved into AccessPanel with the controls that call
     them. The read above stays, because the step's own state — done, to do, or
     blocked on a refusal — is worked out from it. Same query key, so the panel
     and this share one request rather than making two. */

  const handOver = useMutation({

    meta: { quiet: true },
    mutationFn: (deviceId: string) =>
      svc.devices.assign(deviceId, live!.id, 'Onboarding'),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: qk.devices });
      void qc.invalidateQueries({ queryKey: qk.assignments });
      void qc.invalidateQueries({ queryKey: qk.audit });
      setKit('');
      /* assign() reports a partial failure rather than throwing, so a result
         with problems in it is a success as far as react-query is concerned.
         Surfacing them here is the only place anybody sees them. */
      if (result.problems.length > 0) setHandOverProblems(result.problems);
      else setHandOverProblems([]);
    },
  });
  const [handOverProblems, setHandOverProblems] = useState<string[]>([]);

  const [welcome, setWelcome] = useState<string | null>(null);

  /* What EZ Leave said when Finish was pressed. Shown, not swallowed: a person
     who was enrolled and never reached EZ Leave is a person who cannot book
     leave, and nothing else on screen would say so. */
  const [leaveNote, setLeaveNote] = useState<ILeaveOutcome | null>(null);

  /* Finish says what happened, always. A clean result used to close the drawer in
     silence, which looks exactly like the button never having reached EZ Leave —
     so the person pressing it had no way to tell a success from a failure. One
     toast that says it is working and then says how it went, in place. */
  const toast = useToast();
  const finishToast = useRef<ToastHandle | null>(null);
  /* The welcome email gets the same treatment. Its result used to be a line of small
     text under the button, and a failure that threw (a token that could not be got,
     say) showed nothing at all: the button said "Sending…" for a moment and went
     back, which looks exactly like it having done nothing. */
  const welcomeToast = useRef<ToastHandle | null>(null);

  const setUp = useMutation({

    meta: { quiet: true },
    mutationFn: () =>
      svc.onboarding.onboard({
        upn: live!.upn,
        mail: live!.email,
        displayName: live!.name,
        /* The depot the hub never manages to send. AccessScreen passes
           person.office, but its people search never selects officeLocation —
           User.ReadBasic.All refuses the whole request for it — so office is
           always undefined there and every folder it has ever made is named
           "(sf)" rather than "(Hull)". Blueprint has the depot on the record. */
        depot: live!.loc,
        /* Ask for the welcome whether or not the folder is new. The Function
           only emails a brand new folder otherwise, and after the migration
           most people's folders have existed for a while — without this they
           could never be welcomed at all. */
        sendWelcome: true,
      }),
    onMutate: () => {
      welcomeToast.current = toast(`Sending the welcome email to ${live?.email || live?.upn || 'them'}…`, 'busy');
    },
    onError: (e) => {
      const why = (e as Error).message;
      setWelcome(why);
      welcomeToast.current?.update(`The welcome email was not sent: ${why}`, 'error');
    },
    onSuccess: async (outcome) => {
      setWelcome(outcome.message);
      welcomeToast.current?.update(outcome.message, outcome.emailSent ? 'ok' : 'error');
      /* Stamped only when the email actually went. A 207 is a success for the
         folder and leaves a job nobody else will see, so the register has to
         keep saying it is outstanding. */
      if (outcome.emailSent) {
        /* The full timestamp, not the date. Working through ninety-five people
           over several days, the question is "did I already do this one this
           morning" and a date cannot answer it. */
        await svc.people.update(live!.id, { welcomeSentOn: new Date().toISOString() });
        void qc.invalidateQueries({ queryKey: qk.people });
      }
      void qc.invalidateQueries({ queryKey: qk.audit });
    },
  });

  const revoke = useMutation({

    meta: { quiet: true },
    mutationFn: () => svc.directory.revokeAll(live!.graphId as string),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.directoryAccess(live?.graphId ?? '') });
      void qc.invalidateQueries({ queryKey: qk.audit });
    },
  });

  const takeBack = useMutation({

    meta: { quiet: true },
    mutationFn: async () => {
      /* One at a time rather than in parallel. Each is three writes against
         SharePoint, and a half-failed batch is harder to read than a slow one. */
      for (const d of devices.filter((x) => x.user === live!.id)) {
        await svc.devices.assign(d.id, null, 'Leaver');
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.devices });
      void qc.invalidateQueries({ queryKey: qk.assignments });
      void qc.invalidateQueries({ queryKey: qk.audit });
    },
  });

  const closeFolder = useMutation({

    meta: { quiet: true },
    mutationFn: () => svc.onboarding.offboard(live!.upn, live!.email),
    onSuccess: (outcome) => {
      setWelcome(outcome.message);
      void qc.invalidateQueries({ queryKey: qk.audit });
    },
  });

  /* The same rule for both buttons: the step itself is stamped first and is what
     matters, then EZ Leave is told. EZ Leave failing leaves the person correctly
     onboarded or wound down and says so beside the button; it never undoes it. A
     clean result closes the drawer as before, and anything else holds it open so
     the note is read. */
  const afterLeave = (outcome: ILeaveOutcome): void => {
    void qc.invalidateQueries({ queryKey: qk.people });
    void qc.invalidateQueries({ queryKey: qk.audit });
    const bad = outcome.kind === 'failed' || outcome.kind === 'skipped';
    const clean = outcome.problems.length === 0 && !bad;
    finishToast.current?.update(
      `${live?.name ?? 'They'} ${leaving ? 'is offboarded' : 'is set up'}. EZ Leave: ${outcome.message}`,
      bad || !clean ? 'error' : 'ok',
    );
    if (clean) onClose();
    else setLeaveNote(outcome);
  };
  const stepFailed = (e: unknown): void =>
    finishToast.current?.update(`Could not finish: ${(e as Error).message}`, 'error');

  /* The wind-down is not a mutation of its own any more: the panel runs the whole
     of it (sign-in, sessions, hub access, groups, folder, EZ Leave, the stamp) and
     shows each step as it goes. This only says whether to show it. */
  const [winding, setWinding] = useState(false);

  const finish = useMutation({

    meta: { quiet: true },
    mutationFn: async (): Promise<ILeaveOutcome> => {
      await svc.people.update(live!.id, { onboarded: new Date().toISOString().slice(0, 10) });
      return svc.leave.onboard(leaveSourceFor(live!, people.data ?? [], svc.depots.name(live!.loc)));
    },
    onMutate: () => {
      finishToast.current = toast('Finishing the enrolment, then telling EZ Leave…', 'busy');
    },
    onSuccess: afterLeave,
    onError: stepFailed,
  });

  if (!live) return null;

  const leaving = isOffboarding(live);
  /* A read that did not happen has to say so. Without a graphId the sync has
     never matched this row to an Entra account, which is a different problem
     from a refusal and needs a different fix. */
  const reading = {
    level: directory.data?.level,
    problem: !live.graphId
      ? 'The sync has never matched this row to an Entra account, so there is nothing to read access from.'
      : directory.isError
        ? `Could not read their access: ${String((directory.error as Error).message)}`
        : undefined,
  };

  const steps = onboardingSteps(live, devices, live.id, reading);
  const leaverSteps = offboardingSteps(live, devices, live.id, reading);
  const all = personFields(depots, valuesSeen(people.data ?? [], ['title', 'dept']));
  const fieldsFor = (ids: string[]): RenderableField[] =>
    ids.map((id) => all.find((f) => f.id === id)).filter((f): f is RenderableField => !!f);

  const set = (id: string, v: unknown): void => setValues((prev) => ({ ...prev, [id]: v }));
  const dirty = (ids: string[]): boolean =>
    ids.some((id) => (values[id] ?? '') !== ((live as unknown as Record<string, unknown>)[id] ?? ''));

  return (
    <Drawer
      open
      onClose={onClose}
      title={live.name}
      sub={leaving ? 'Leaving' : 'Being set up'}
      meta={live.email || live.upn}
    >
      {/* Shown for as long as it is open, NOT only while they are still "leaving": the
          last thing the wind-down does is stamp them as wound down, which takes
          them out of that state, and the panel must not vanish before its own
          summary has been read. */}
      {winding ? (
        <WindDownPanel
          person={live}
          people={people.data ?? []}
          depotName={svc.depots.name(live.loc)}
          lastDay={live.leavingOn || new Date().toISOString().slice(0, 10)}
          onCancel={() => setWinding(false)}
          onFinished={() => { setWinding(false); onClose(); }}
        />
      ) : leaving ? (
        <>
          {leaverSteps.map((step) => {
            if (step.id === 'revoke') {
              return (
                <StepRow key={step.id} step={step}>
                  {step.state === 'todo' ? (
                    <Button primary disabled={revoke.isPending} onClick={() => revoke.mutate()}>
                      {revoke.isPending ? 'Removing...' : 'Remove all hub and app access'}
                    </Button>
                  ) : null}
                  {revoke.error ? (
                    <div className={s.stepError}>{String((revoke.error as Error).message)}</div>
                  ) : null}
                  <div className={s.ownedNote}>
                    Their Entra account, mailbox and licence are not touched. Those are done in
                    Entra as part of the normal leaver process.
                  </div>
                </StepRow>
              );
            }

            if (step.id === 'kit') {
              return (
                <StepRow key={step.id} step={step}>
                  {step.state === 'todo' ? (
                    <Button primary disabled={takeBack.isPending} onClick={() => takeBack.mutate()}>
                      {takeBack.isPending ? 'Taking back...' : 'Take their equipment back'}
                    </Button>
                  ) : null}
                  {/* Unassigned, history kept. "Who had this laptop in March?"
                      has to stay answerable after they have gone, which is
                      exactly when somebody starts asking. */}
                  <div className={s.ownedNote}>
                    The devices go back to unassigned and the record of who held them stays, so a
                    laptop that comes back damaged can still be traced.
                  </div>
                </StepRow>
              );
            }

            if (step.id === 'folder') {
              return (
                <StepRow key={step.id} step={step}>
                  <Button primary disabled={closeFolder.isPending} onClick={() => closeFolder.mutate()}>
                    {closeFolder.isPending ? 'Closing...' : 'Close their folder'}
                  </Button>
                  {welcome ? <div className={s.stepDetail}>{welcome}</div> : null}
                </StepRow>
              );
            }

            if (step.id === 'retention') {
              return (
                <StepRow key={step.id} step={step}>
                  <FormBlock
                    fields={fieldsFor(['keepUntil'])}
                    values={values}
                    onChange={set}
                    lookups={{ people: [], suppliers: [] }}
                  />
                  <Button
                    disabled={!dirty(['keepUntil']) || save.isPending}
                    onClick={() => save.mutate({ keepUntil: String(values.keepUntil ?? '') })}
                  >
                    {save.isPending ? 'Saving...' : 'Save'}
                  </Button>
                </StepRow>
              );
            }

            /* The honest hand-off. Blueprint cannot revoke a department library
               grant, reclaim a licence or touch a SharePoint site group, and a
               flow that closed with those outstanding would be worse than no
               flow: somebody would press Finish believing it was done. */
            return (
              <StepRow key={step.id} step={step}>
                <div className={s.ownedNote}>
                  Department document libraries, the licence and SharePoint&rsquo;s own site groups
                  are not Blueprint&rsquo;s to remove. Finish those in Hub Admin under Document
                  libraries, and in Entra.
                </div>
              </StepRow>
            );
          })}

          <div className={s.stepRow}>
            <Button
              primary
              disabled={leaverSteps.find((x) => x.id === 'kit')?.state !== 'done'}
              onClick={() => setWinding(true)}
            >
              Offboard…
            </Button>
            <div className={s.stepDetail}>
              {leaverSteps.find((x) => x.id === 'kit')?.state === 'done'
                ? 'Shows you exactly what will happen first, and changes nothing until you confirm. It blocks their sign-in, removes their access and groups, closes their folder and marks them a leaver in EZ Leave.'
                : 'Collect their equipment first. Everything else is done for you.'}
            </div>
          </div>
        </>
      ) : (
        <>
          {steps.map((step) => {
            if (step.id === 'classify') {
              return (
                <StepRow key={step.id} step={step}>
                  <FormBlock fields={fieldsFor(['kind'])} values={values} onChange={set} lookups={{ people: [], suppliers: [] }} />
                  <Button
                    primary={step.state === 'todo'}
                    disabled={!values.kind || !dirty(['kind']) || save.isPending}
                    onClick={() => save.mutate({ kind: values.kind as IPerson['kind'] })}
                  >
                    {save.isPending ? 'Saving…' : 'Save'}
                  </Button>
                </StepRow>
              );
            }

            if (step.id === 'details') {
              const ids = ['loc', 'mobile', 'start', 'title', 'dept'];
              return (
                <StepRow key={step.id} step={step}>
                  <FormBlock fields={fieldsFor(ids)} values={values} onChange={set} lookups={{ people: [], suppliers: [] }} />
                  <Button
                    primary={step.state === 'todo'}
                    disabled={!dirty(ids) || save.isPending}
                    onClick={() =>
                      save.mutate({
                        loc: String(values.loc ?? ''),
                        mobile: String(values.mobile ?? ''),
                        start: String(values.start ?? ''),
                        title: String(values.title ?? ''),
                        dept: String(values.dept ?? ''),
                      })
                    }
                  >
                    {save.isPending ? 'Saving…' : 'Save'}
                  </Button>
                  {/* Depot became Entra's on 06/10/2026. A value typed here
                      stands only while Entra has none; a different officeLocation
                      upstream puts its own back at 02:00. */}
                  <div className={s.ownedNote}>
                    Depot is written by the nightly sync from the account&rsquo;s office in Entra. Setting it
                    here fills a blank; if Entra holds a different depot, Entra wins tonight.
                  </div>
                </StepRow>
              );
            }

            if (step.id === 'access') {
              /* The panel, not the controls. It was written here and moved out
                 on 08/10/2026 so the Accounts drawer could show the same thing
                 — see AccessPanel for why one component rather than two. */
              return (
                <StepRow key={step.id} step={step}>
                  <AccessPanel graphId={live?.graphId} name={live?.name} email={live?.email} />
                </StepRow>
              );
            }

            if (step.id === 'folder') {
              return (
                <StepRow key={step.id} step={step}>
                  <Button
                    primary={step.state === 'todo'}
                    disabled={setUp.isPending}
                    onClick={() => setUp.mutate()}
                  >
                    {setUp.isPending ? 'Sending…' : 'Send welcome email and create folder'}
                  </Button>
                  {welcome ? <div className={s.stepDetail}>{welcome}</div> : null}
                  {/* This warning changed meaning with sendWelcome. It used to
                      say "safe to press twice", which was true while the
                      Function only emailed a brand new folder. It sends every
                      time it is asked now, which is the point of the flag — but
                      it means a second press is a second email. */}
                  <div className={s.ownedNote}>
                    This sends every time it is pressed, so pressing it twice welcomes them twice.
                    The time it last went is recorded against them. The folder is named for their
                    depot, so set that first.
                  </div>
                </StepRow>
              );
            }

            if (step.id === 'equipment') {
              /* traitsFor().assignable, not a list of types typed out here.
                 The register already decides what can belong to a person — it
                 is the same test that hides the "Assigned to" field on a
                 firewall — and a second opinion about it would drift. Without
                 it the picker offered a UPS and three FortiGates. */
              const free = devices
                .filter((d) => !d.user && traitsFor(d.type).assignable)
                .sort((a, b) => a.tag.localeCompare(b.tag));
              const theirs = devices.filter((d) => d.user === live.id);
              return (
                <StepRow key={step.id} step={step}>
                  {theirs.length > 0 ? (
                    <div className={s.stepDetail}>
                      {theirs.map((d) => `${d.tag} — ${d.make} ${d.model}`).join(', ')}
                    </div>
                  ) : null}
                  {free.length === 0 ? (
                    <div className={s.stepDetail}>
                      Nothing unassigned to hand over. Add the device to the register first.
                    </div>
                  ) : (
                    <>
                      <select
                        className={s.stepSelect}
                        value={kit}
                        onChange={(e) => setKit(e.target.value)}
                        aria-label="Device to hand over"
                      >
                        <option value="">Choose a device…</option>
                        {free.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.tag} — {d.make} {d.model} ({d.type})
                          </option>
                        ))}
                      </select>
                      <Button
                        primary={step.state === 'todo'}
                        disabled={!kit || handOver.isPending}
                        onClick={() => handOver.mutate(kit)}
                      >
                        {handOver.isPending ? 'Assigning…' : 'Hand it over'}
                      </Button>
                    </>
                  )}
                  {handOverProblems.length > 0 ? (
                    <div className={s.stepError}>
                      {handOverProblems.join(' ')} The device page will show that the history and
                      the assignment do not match until it is put right.
                    </div>
                  ) : null}
                </StepRow>
              );
            }

            return <StepRow key={step.id} step={step} />;
          })}

          {save.error ? <div className={s.stepError}>{String((save.error as Error).message)}</div> : null}

          <div className={s.stepRow}>
            <Button
              primary
              disabled={!canFinishOnboarding(steps) || finish.isPending}
              onClick={() => finish.mutate()}
            >
              {finish.isPending ? 'Finishing…' : 'Finish — they are set up'}
            </Button>
            {/* The hint has to match what is actually in front of them. Offering
                "a laptop that arrives next week" to a shared mailbox is the same
                fault as telling somebody a laptop has a sign-in address. */}
            <div className={s.stepDetail}>
              {!canFinishOnboarding(steps)
                ? 'Say what kind of account this is and fill in their depot and start date first.'
                : steps.some((x) => x.id === 'equipment')
                  ? 'Equipment can be outstanding — a laptop that arrives next week does not stop somebody being set up.'
                  : 'Classifying it is all this account needs.'}
            </div>
          </div>

          {/* The override for somebody made by hand. Under Finish rather than instead of it: Finish
              creates their EZ Leave record, this deliberately creates nothing. */}
          <AlreadySetUp person={live} onDone={onClose} />

          {/* Creates them in SignIn, the Training Matrix and EZ Leave, for somebody set up some other way.
              The Function checks before it writes, so it is safe for somebody who is already in one. */}
          {isRealPerson(live) ? <SystemsPanel person={live} depotName={svc.depots.name(live.loc)} /> : null}
        </>
      )}

      {leaveNote ? (
        <div className={s.stepRow}>
          <div className={leaveNote.kind === 'failed' || leaveNote.kind === 'skipped' ? s.stepError : s.stepDetail}>
            <b>EZ Leave:</b> {leaveNote.message}
            {leaveNote.problems.length > 0 ? (
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {leaveNote.problems.map((p) => <li key={p}>{p}</li>)}
              </ul>
            ) : null}
          </div>
          <Button primary onClick={onClose}>Done</Button>
        </div>
      ) : null}
    </Drawer>
  );
}
